import { useEffect, useState } from "react";
import { Bell } from "lucide-react";
import dayjs from "dayjs";

import ToastMsg from "../../../components/common/ToastMsg";
import useApiMethods from "../../../lib/useApiMethods";
import { useNotifications } from "../../../context/NotificationContext";
import { NOTIFICATION_LOG } from "../../../constants/api";
import { cn } from "../../../utils/tailwindUtils";

const MyProfileNotificationsTab = () => {
    const { fetch } = useApiMethods();
    const { recentNotification } = useNotifications();
    const [loading, setLoading] = useState(false);
    const [items, setItems] = useState([]);

    useEffect(() => {
        const load = async () => {
            setLoading(true);
            try {
                const res = await fetch(NOTIFICATION_LOG);
                if (res?.status === "success") {
                    setItems(res.data || []);
                } else {
                    ToastMsg("error", res?.message || "Failed to load notifications");
                }
            } catch (err) {
                ToastMsg("error", err?.message || "Failed to load notifications");
            } finally {
                setLoading(false);
            }
        };
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!recentNotification?.id) return;
        setItems((prev) => {
            if (prev.some((item) => item.id === recentNotification.id)) {
                return prev;
            }
            return [
                {
                    id: recentNotification.id,
                    title: recentNotification.title,
                    message: recentNotification.message,
                    time: recentNotification.time,
                    status: recentNotification.is_opened ? "read" : "unread",
                    notification_action: recentNotification.notification_action,
                },
                ...prev,
            ];
        });
    }, [recentNotification]);

    if (loading) {
        return (
            <div className="flex items-center justify-center h-48">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sidebar-primary" />
            </div>
        );
    }

    return (
        <div className="space-y-4">
            <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                    <Bell className="h-5 w-5 text-sidebar-primary" />
                    Notification Log
                </h3>
                <p className="text-sm text-muted-foreground mt-1">
                    Alerts and updates sent to you — leave approvals, attendance notices, and system messages.
                </p>
            </div>

            {items.length === 0 ? (
                <p className="text-sm text-gray-400 italic py-8 text-center border border-dashed border-gray-200 dark:border-gray-700 rounded-xl">
                    No notifications yet.
                </p>
            ) : (
                <div className="max-h-[min(520px,calc(100vh-320px))] overflow-y-auto pr-1 space-y-2">
                    {items.map((item) => (
                        <div
                            key={item.id}
                            className={cn(
                                "rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900/30 p-4",
                                item.status === "unread" && "border-sidebar-primary/30 bg-sidebar-primary/5",
                            )}
                        >
                            <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                    <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                                        {item.title || "Notification"}
                                    </p>
                                    <p className="text-sm text-gray-700 dark:text-gray-300 mt-1 whitespace-pre-wrap break-words">
                                        {item.message}
                                    </p>
                                    <p className="text-xs text-muted-foreground mt-2">
                                        {item.time ? dayjs(item.time).format("DD MMM YYYY, hh:mm A") : "—"}
                                        {item.notification_action ? ` · ${item.notification_action}` : ""}
                                    </p>
                                </div>
                                {item.status && (
                                    <span className="text-[10px] uppercase font-bold tracking-wide px-2 py-0.5 rounded-full bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 shrink-0">
                                        {item.status}
                                    </span>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default MyProfileNotificationsTab;
